import Axios from 'axios';

// Internal types
export type GooglePlaceAutocomplete = {
  place_id: string;
  description: string;
};

export type GooglePlaceDetails = {
  country?: string;
  city?: string;
  region?: string;
  zipCode?: string;
  geolocation?: {
    lat: number;
    lng: number;
  };
};

class GooglePlacesService {
  public async autocomplete(
    query: string,
    limit?: number,
  ): Promise<GooglePlaceAutocomplete[]> {
    const params = new URLSearchParams({
      input: query,
      language: 'en',
      types: '(cities)',
    });
    if (limit) {
      params.set('limit', limit.toString());
    }
    const res = await Axios.get(`/api/places/autocomplete?${params}`);

    // Google answers HTTP 200 with statuses like REQUEST_DENIED and omits
    // `predictions`. Returning that undefined list crashed the typeahead.
    const predictions = res.data?.predictions;
    if (!Array.isArray(predictions)) {
      const status = res.data?.status ? ` (${res.data.status})` : '';
      throw new Error(`Places autocomplete returned no predictions${status}`);
    }

    return predictions;
  }

  public async getDetails(placeId: string): Promise<GooglePlaceDetails> {
    const params = new URLSearchParams({
      place_id: placeId,
      fields: 'address_components,geometry',
    });
    const res = await Axios.get(`/api/places/details?${params}`);
    const address_components: {
      long_name: string;
      short_name: string;
      types: string[];
    }[] = res.data.result.address_components;

    const country = address_components.find(component =>
      component.types.includes('country'),
    )?.short_name;

    const region = address_components.find(component =>
      component.types.includes('administrative_area_level_1'),
    )?.short_name;

    // Note: I called find twice in case there's some odd scenerio where there is a
    // locality and administrative_area_level_3.  I don't want to pick the latter
    // by mistake.
    const city = (
      address_components.find(component => component.types.includes('locality')) ??
      /* Many schools/districts are in townships or another level up from
         from locality, so use that as a fallback.
         Example: Canton, MI is actually a township:
        [
          {
            long_name: 'Canton',
            short_name: 'Canton',
            types: [ 'administrative_area_level_3', 'political' ]
          },
          {
            long_name: 'Wayne County',
            short_name: 'Wayne County',
            types: [ 'administrative_area_level_2', 'political' ]
          },
          {
            long_name: 'Michigan',
            short_name: 'MI',
            types: [ 'administrative_area_level_1', 'political' ]
          },
          {
            long_name: 'United States',
            short_name: 'US',
            types: [ 'country', 'political' ]
          }
        ] 
      */
      address_components.find(component =>
        component.types.includes('administrative_area_level_3'),
      )
    )?.long_name;

    let zipCode: string | undefined;
    let geolocation:
      | {
          lat: number;
          lng: number;
        }
      | undefined;
    if (country === 'US') {
      zipCode = address_components.find(component =>
        component.types.includes('postal_code'),
      )?.short_name;

      const {
        location,
      }: { location: { lat: number; lng: number } } = res.data.result.geometry;

      geolocation = location;

      if (!zipCode && geolocation) {
        const { lat, lng } = geolocation;
        const {
          data: { results },
        } = await Axios.get(
          `/api/geocode/details?${new URLSearchParams({
            latlng: `${lat},${lng}`,
            result_type: 'postal_code',
          })}`,
        );

        const geocodeAddressComponents: {
          long_name: string;
          short_name: string;
          types: string[];
        }[] = results?.length > 0 ? results[0].address_components : [];

        zipCode = geocodeAddressComponents.find(component =>
          component.types.includes('postal_code'),
        )?.short_name;
      }
    }

    return {
      country,
      region,
      city,
      zipCode,
      geolocation,
    };
  }
}

export default new GooglePlacesService();
